"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchPublicPage, fetchPageByDomain } from "../../api/queries";

export default function PublicPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [pageId, setPageId] = useState(null);

  const { storeId } = useParams();

  // Check if we're on a custom domain
  const isCustomDomain =
    !window.location.hostname.includes("loyaltify.io") &&
    !window.location.hostname.includes("localhost") &&
    !window.location.hostname.includes("127.0.0.1");

  // Fetch page by domain if on custom domain
  const { data: domainPageData } = useQuery({
    queryKey: ["pageByDomain", window.location.hostname],
    queryFn: () => fetchPageByDomain(window.location.hostname),
    enabled: isCustomDomain,
  });

  // Set page ID from domain lookup
  useEffect(() => {
    if (domainPageData?.page_id) {
      setPageId(domainPageData.page_id);
    }
  }, [domainPageData]);

  const { data: publicPage } = useQuery({
    queryKey: ["publicPage", isCustomDomain ? pageId : storeId],
    queryFn: () => fetchPublicPage(isCustomDomain ? pageId : storeId),
    enabled: isCustomDomain ? !!pageId : !!storeId,
  });

  // Check for data parameter in URL hash for preview
  useEffect(() => {
    const parseHashData = () => {
      const hash = window.location.hash.slice(1); // Remove the # character

      if (hash) {
        try {
          const parsedData = JSON.parse(decodeURIComponent(hash));
          setPreviewData(parsedData);
        } catch (error) {
          console.error("Failed to parse hash data:", error);
        }
      } else {
        setPreviewData(null); // Clear preview data if no hash
      }
    };

    // Parse initial hash data
    parseHashData();

    // Listen for hash changes
    const handleHashChange = () => {
      parseHashData();
    };

    window.addEventListener("hashchange", handleHashChange);

    // Cleanup event listener
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Use preview data if available, otherwise use fetched data
  const pageData = previewData || publicPage?.data;
  const storeInfo = publicPage?.store;
  const passInfo = publicPage?.pass;
  const pageSettings = publicPage?.settings;

  console.log("Using data:", previewData ? "preview" : "fetched", pageData);

  return (
    <div>
      <header
        className="absolute inset-x-0 top-0 z-50"
        style={{
          backgroundColor: `${pageData?.header?.background_color}`,
          color: `${pageData?.header?.text_color}`,
        }}
      >
        <nav
          aria-label="Global"
          className="flex items-center justify-between lg:px-8 px-4"
        >
          <div className="flex lg:flex-1 h-12 lg:h-24 items-center">
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">{storeInfo?.name}</span>
              <img
                alt=""
                src={storeInfo?.logo_url}
                className="lg:h-12 h-8 w-auto"
              />
            </a>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="hidden lg:flex lg:gap-x-12">
            {pageData?.header?.navigation?.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-sm/6 font-semibold"
              >
                {item.name}
              </a>
            ))}
          </div>
          <div className="hidden lg:flex lg:flex-1 lg:justify-end">
            {/* Carry the query string over to the form: a campaign link that
                lands on the club page rather than straight on /join still
                holds the parameters the form's hidden fields read. */}
            <a
              href={`/join${window.location.search}`}
              className="text-sm/6 font-semibold"
            >
              {pageData?.header?.login_text}{" "}
              <span aria-hidden="true">&rarr;</span>
            </a>
          </div>
        </nav>
        <Dialog
          open={mobileMenuOpen}
          onClose={setMobileMenuOpen}
          className="lg:hidden"
        >
          <div className="fixed inset-0 z-50" />
          <DialogPanel
            className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto p-6 sm:max-w-sm sm:ring-1"
            style={{
              backgroundColor: `${pageData?.header?.background_color}`,
              color: `${pageData?.header?.text_color}`,
            }}
          >
            <div className="flex items-center justify-between">
              <a href="#" className="-m-1.5 p-1.5">
                <span className="sr-only">{storeInfo?.name}</span>
                <img alt="" src={storeInfo?.logo_url} className="h-8 w-auto" />
              </a>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2.5 rounded-md p-2.5"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {pageData?.header?.navigation?.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold hover:bg-gray-50"
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
                <div className="py-6">
                  <a
                    href="#"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold hover:bg-gray-50"
                  >
                    Log in
                  </a>
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      {/* Hero */}
      <div
        className="relative isolate pt-14"
        style={{
          backgroundColor: `${pageData?.hero?.background_color}`,
          color: `${pageData?.hero?.text_color}`,
        }}
      >
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:flex lg:items-center lg:gap-x-10 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:flex-auto">
            <h1 className="text-5xl font-semibold tracking-tight text-pretty sm:text-7xl">
              {pageData?.hero?.title}
            </h1>
            <p className="mt-8 text-lg font-medium text-pretty sm:text-xl/8">
              {pageData?.hero?.description}
            </p>
            <div className="mt-10 flex items-center gap-x-6">
              <a
                href="#"
                style={{
                  backgroundColor: `${pageData?.hero?.button?.background_color}`,
                  color: `${pageData?.hero?.button?.text_color}`,
                }}
                className="rounded-md px-3.5 py-2.5 text-sm font-semibold shadow-2xs focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                {pageData?.hero?.button?.text}
              </a>
              <a href="#" className="text-sm/6 font-semibold">
                Learn more <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
          <div className="mt-16 sm:mt-24 lg:mt-0 p-12">
            <img
              alt=""
              src={passInfo?.preview_image_url}
              className="max-w-full h-auto max-h-96 lg:max-h-[500px] object-contain"
            />
          </div>
        </div>
      </div>

      {/* Call to action */}
      <div
        className="py-24 sm:py-32 overflow-wrap:anywhere"
        style={{
          backgroundColor: `${pageData?.call_to_action?.background_color}`,
          color: `${pageData?.call_to_action?.text_color}`,
        }}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-16 lg:grid-cols-1">
            <div className="flex flex-col gap-y-4">
              <div
                className="rich-text"
                dangerouslySetInnerHTML={{
                  __html: pageData?.call_to_action?.content,
                }}
              />
            </div>
          </dl>
        </div>
      </div>

      {/* How it works */}
      <div
        className="py-24 sm:py-32"
        style={{
          backgroundColor: `${pageData?.how_it_works?.background_color}`,
          color: `${pageData?.how_it_works?.text_color}`,
        }}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <p className="mt-2 text-4xl font-semibold tracking-tight text-pretty sm:text-5xl lg:text-balance">
              {pageData?.how_it_works?.title}
            </p>
            <p className="mt-6 text-lg/8">
              {pageData?.how_it_works?.description}
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {pageData?.how_it_works?.steps?.map((feature) => (
                <div key={feature.name} className="flex flex-col">
                  <dt className="flex items-center gap-x-3 text-base/7 font-semibold">
                    <img
                      src={feature.image_url}
                      alt={feature.title}
                      className="size-5 flex-none "
                    />
                    {feature.title}
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base/7 ">
                    <p className="flex-auto">{feature.description}</p>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div
        className="py-24 sm:py-32"
        style={{
          backgroundColor: `${pageData?.benefits?.background_color}`,
          color: `${pageData?.benefits?.text_color}`,
        }}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-16 lg:grid-cols-1">
            <div className="flex flex-col gap-y-4">
              <div
                className="rich-text"
                dangerouslySetInnerHTML={{
                  __html: pageData?.benefits?.content,
                }}
              />
            </div>
          </dl>
        </div>
      </div>

      {/* Campaigns */}
      <div
        className="py-24 sm:py-32"
        style={{
          backgroundColor: `${pageData?.campaign?.background_color}`,
          color: `${pageData?.campaign?.text_color}`,
        }}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <p className="mt-2 text-4xl font-semibold tracking-tight text-pretty sm:text-5xl lg:text-balance">
              {pageData?.campaign?.title}
            </p>
            <p className="mt-6 text-lg/8 ">{pageData?.campaign?.description}</p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2 lg:gap-y-16">
              {pageData?.campaign?.items?.map((feature) => (
                <div key={feature.title} className="relative pl-16">
                  <dt className="text-base/7 font-semibold">
                    <div className="absolute top-0 left-0 flex size-10 items-center justify-center rounded-lg bg-indigo-600">
                      <img
                        src={feature.image_url}
                        alt={feature.title}
                        className="size-6 text-white"
                      />
                    </div>
                    {feature.title}
                  </dt>
                  <dd className="mt-2 text-base/7 ">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* Footer */}
      {!pageSettings?.hideFooter && (
        <div
          className="py-3"
          style={{
            backgroundColor: `${pageData?.header?.background_color}`,
            color: `${pageData?.header?.text_color}`,
          }}
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="flex items-center gap-x-2 text-sm font-medium opacity-80">
                <span>Loyalty program by</span>
                <a
                  href="https://loyaltify.io"
                  title="Customer Loyalty Platform & Wallet Cards"
                  target="_blank"
                  className="inline-flex items-center gap-x-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 group"
                >
                  <img
                    src="https://media.loyaltify.io/static/logo/128.png"
                    alt="Loyaltify"
                    className="size-5 transition-transform duration-200 group-hover:scale-110"
                  />
                  <span className="font-semibold">Loyaltify</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
